import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  Activity,
  AlertCircle,
  ArrowLeftRight,
  Building2,
  CalendarDays,
  CheckCircle2,
  Clock3,
  CreditCard,
  HardDrive,
  Monitor,
  Smartphone,
  Zap,
} from "lucide-react";
import { Badge, Button } from "@studio";
import { MetricGrid } from "./metrics";
import type { Tone } from "../lib/format";

/** 시안 shared.tsx / console-panels.tsx 의 표시 부품 (예시 데이터 없이). */
export function Brand({ bridge = false }: { bridge?: boolean }) {
  return (
    <span className="meta-crew-brand">
      <span>
        <Activity size={19} />
      </span>
      {/* 2026-10-21 사용자: "Console" 꼬리표는 뜻이 없고 이름이 두 줄로 꺾여 뺐다. 데스크톱 앱만 "Bridge" 를 남긴다 */}
      {/* span 이 아니어야 한다: 시안 CSS 는 .meta-crew-brand > span 을 30px 아이콘 상자로 그린다 */}
      <b style={{ whiteSpace: "nowrap", font: "inherit" }}>Meta Crew</b>
      {bridge && <small>Bridge</small>}
    </span>
  );
}

export function Panel({ title, extra, children, className = "" }: { title: string; extra?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`meta-crew-panel ${className}`}>
      <header>
        <h2>{title}</h2>
        {extra}
      </header>
      {children}
    </section>
  );
}

export function InfoRow({ title, description, children }: { title: ReactNode; description?: ReactNode; children?: ReactNode }) {
  return (
    <div className="mcc-info-row">
      <div>
        <strong>{title}</strong>
        {description !== undefined && <small>{description}</small>}
      </div>
      {children}
    </div>
  );
}

export function Tabs({ items, value, onChange }: { items: string[]; value: string; onChange: (s: string) => void }) {
  // 시안 06a1acb: 탭이 많으면 가로 스크롤, 선택된 탭이 보이도록 맞춘다.
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = listRef.current;
    const active = el?.querySelector<HTMLButtonElement>("button[aria-pressed=true]");
    if (el && active) {
      const left = active.offsetLeft - el.offsetLeft;
      if (left < el.scrollLeft || left + active.offsetWidth > el.scrollLeft + el.clientWidth) el.scrollLeft = Math.max(0, left - 16);
    }
  }, [value]);
  return (
    <div ref={listRef} className="meta-crew-tabs" role="group" aria-label="화면 분류">
      {items.map((s) => (
        <button key={s} type="button" aria-pressed={s === value} className={s === value ? "active" : ""} onClick={() => onChange(s)}>
          {s}
        </button>
      ))}
    </div>
  );
}

const metricIcons: Record<string, typeof Activity> = {
  고객사: Building2,
  "온라인 머신": Monitor,
  "등록 디바이스": Smartphone,
  "연결된 디바이스": Smartphone,
  디바이스: Smartphone,
  "실패 실행 · 24h": AlertCircle,
  "오늘 실패": AlertCircle,
  "확인 필요": AlertCircle,
  보류: Clock3,
  "유예 고객사": CalendarDays,
  "정지 고객사": AlertCircle,
  "이번 달 작업": Activity,
  "LLM 작업": Activity,
  "토큰 사용": Zap,
  "저장 용량": HardDrive,
  트래픽: ArrowLeftRight,
  "다음 청구": CreditCard,
  "서비스 상태": CheckCircle2,
  서비스: CheckCircle2,
  대기: Clock3,
  "진행 중": Activity,
  "구독 기간": CalendarDays,
};
export type Stat = [label: string, value: string | number, note: string, tone?: Tone];
/** 카드 이동 (2026-10-21): 라벨 → 누르면 갈 곳. 권한이 없는 곳은 넣지 않는다. */
export type StatLinks = Partial<Record<string, { onOpen: () => void; label: string } | false | undefined>>;
export function StatGrid({ items, links }: { items: Stat[]; links?: StatLinks }) {
  return (
    <MetricGrid
      items={items.map(([label, value, note, tone]) => {
        const Icon = metricIcons[label] ?? Activity;
        const link = links?.[label] || undefined;
        return { label, value, note, icon: <Icon />, tone: tone ?? "blue", onOpen: link?.onOpen, openLabel: link?.label };
      })}
    />
  );
}

export function StatusBadge({ label }: { label: [string, Tone] }) {
  return <Badge tone={label[1]}>{label[0]}</Badge>;
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="mcc-empty">{children}</div>;
}

export function Loading({ label = "불러오는 중" }: { label?: string }) {
  return <div className="mcc-loading">{label}…</div>;
}

export function ErrorBox({ error, onRetry }: { error: string; onRetry?: () => void }) {
  return (
    <div className="mcc-banner mcc-banner-danger">
      <AlertCircle size={20} />
      <div>
        <strong>불러오지 못했습니다</strong>
        <p>{error}</p>
      </div>
      {onRetry && (
        <Button size="sm" variant="secondary" onClick={onRetry}>
          다시 시도
        </Button>
      )}
    </div>
  );
}

/** 패널 안 오류 (시안 06a1acb PanelError): 패널 본문 여백 안에, 테두리 없는 오류 배경 + 다시 시도. 페이지 단위 오류는 ErrorBox. */
export function PanelError({ error, onRetry }: { error?: string; onRetry?: () => void }) {
  return (
    <div className="lwd-panel-body">
      <div className="lwd-panel-error" role="alert">
        <AlertCircle size={20} />
        <div>
          <strong>불러오지 못했습니다</strong>
          <p>{error || "일시적인 연결 오류입니다. 잠시 후 다시 시도해 주세요."}</p>
          {onRetry && (
            <Button size="sm" variant="secondary" onClick={onRetry}>
              다시 시도
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/** 아직 연동 전인 화면. 예시 데이터를 실제처럼 보이지 않게 한다. */
export function NotReady({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <Panel title={title} extra={<Badge>준비 중</Badge>}>
      <InfoRow title="이 화면은 다음 단계에서 연결됩니다" description={children ?? "지금은 실제 데이터가 없습니다."} />
    </Panel>
  );
}

export function Pager({ offset, limit, total, onChange }: { offset: number; limit: number; total: number; onChange: (offset: number) => void }) {
  if (total <= limit) return null;
  return (
    <div className="mcc-pager">
      <span>
        {offset + 1}–{Math.min(offset + limit, total)} / {total}건
      </span>
      <div className="meta-crew-buttons">
        <Button size="sm" variant="secondary" disabled={offset === 0} onClick={() => onChange(Math.max(0, offset - limit))}>
          이전
        </Button>
        <Button size="sm" variant="secondary" disabled={offset + limit >= total} onClick={() => onChange(offset + limit)}>
          다음
        </Button>
      </div>
    </div>
  );
}

export function CopyButton({ text, label = "복사" }: { text: string; label?: string }) {
  const [done, setDone] = useState(false);
  return (
    <Button
      size="sm"
      variant="secondary"
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => {
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        });
      }}
    >
      {done ? "복사됨" : label}
    </Button>
  );
}
